// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Tipovi i konstante
import { LeasingSearchPageCmp, LeasingSearchPageProps } from "./types";
import {
  LeasingRow,
  LeasingSearchFormValues,
} from "services/api/calculations/leasing/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  CALCULATION_LEASING_CREATE_PAGE_TITLE,
  CALCULATION_LEASING_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// API servisi
import { getLeasingsAPI } from "services/api/calculations/leasing/api";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Modal } from "ui/components/Modal";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { NoResults } from "ui/components/NoResults";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import TabMenu from "../TabMenu/TabMenu";
import LeasingEditCard from "../LeasingEditCard/LeasingEditCard";

// Lokalne komponente
import LeasingSearchFilters from "./components/LeasingSearchFilters/LeasingSearchFilters";
import LeasingSearchResults from "./components/LeasingSearchResults/LeasingSearchResults";

// Helperi
import { transformRoute, useTranslate } from "helpers";

// URL i navigacija
import { Routes } from "ui/modules/calculation/navigation";

const LeasingSearchPage: LeasingSearchPageCmp = (
  props: LeasingSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<LeasingRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [leasingId, setLeasingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getLeasingsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.CalculationLeasingDetailPageRouter, {
        leasingId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setLeasingId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: LeasingSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setLeasingId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_LEASING_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_LEASING_SEARCH_PAGE_TITLE.icon}
              iconColor={CALCULATION_LEASING_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <LeasingSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t("admin.calculation.setting.leasing.search.noresults")}
            />
          ) : (
            <LeasingSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          <ModalHeader
            title={CALCULATION_LEASING_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <LeasingEditCard
              leasingId={leasingId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

LeasingSearchPage.route = Routes.CalculationLeasingSearchPageRouter;

export default LeasingSearchPage;
